import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { 
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Circle,
  AlertCircle,
  Trophy,
  XCircle,
  RotateCcw,
  Home,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
const API = `${API_BASE}/api`;

export default function QuizPlayer() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [quiz, setQuiz] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  const timerRef = useRef(null);

  useEffect(() => {
    startQuiz();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [quizId]);

  useEffect(() => {
    // Timer logic
    if (timeRemaining !== null && timeRemaining > 0 && !results) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timerRef.current);
    }
  }, [timeRemaining, results]);

  const startQuiz = async () => {
    try {
      const res = await axios.post(`${API}/quizzes/${quizId}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuiz(res.data.attempt.questions ? { ...res.data.attempt } : null);
      setAttempt(res.data.attempt);
      
      // Set timer if time limit exists
      if (res.data.time_limit_minutes) {
        setTimeRemaining(res.data.time_limit_minutes * 60);
      }
      
      // Load any previously saved answers
      if (res.data.attempt.answers) {
        setAnswers(res.data.attempt.answers);
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Failed to start quiz';
      toast.error(errorDetail);
      if (error.response?.status === 400 && errorDetail.includes('Maximum attempts')) {
        // User has exhausted attempts, redirect back
        setTimeout(() => navigate(-1), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveAnswer = async (questionId, answerData) => {
    try {
      await axios.post(
        `${API}/quizzes/${quizId}/attempts/${attempt.attempt_id}/answer`,
        { question_id: questionId, ...answerData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to save answer:', error);
    }
  };

  const handleAnswerChange = (questionId, answerData) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerData
    }));
    
    // Auto-save answer to server
    saveAnswer(questionId, answerData);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/quizzes/${quizId}/attempts/${attempt.attempt_id}/submit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResults(res.data);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (res.data.passed) {
        toast.success('Congratulations! You passed the quiz!');
      } else {
        toast.info('Quiz submitted. Review your results below.');
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentQuestion = attempt?.questions?.[currentQuestionIndex];
  const totalQuestions = attempt?.questions?.length || 0;
  const answeredCount = Object.keys(answers).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-white">Loading quiz...</div>
      </div>
    );
  }

  if (!attempt || !attempt.questions) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Quiz Not Available</h2>
          <p className="text-zinc-400 mb-6">This quiz could not be loaded.</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Results Screen
  if (results) {
    return (
      <div className="min-h-screen bg-navigate-dark" data-testid="quiz-results">
        <div className="max-w-3xl mx-auto px-4 py-12">
          {/* Results Header */}
          <div className="text-center mb-8">
            {results.passed ? (
              <Trophy className="h-20 w-20 text-navigate-gold mx-auto mb-4" />
            ) : (
              <XCircle className="h-20 w-20 text-red-400 mx-auto mb-4" />
            )}
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              {results.passed ? 'Quiz Passed!' : 'Quiz Complete'}
            </h1>
            <p className="text-zinc-400">
              {results.passed 
                ? 'Congratulations on completing this assessment!' 
                : 'Review your answers below and try again.'}
            </p>
          </div>

          {/* Score Card */}
          <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 mb-8">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold text-white">{results.percentage?.toFixed(0) || 0}%</p>
                <p className="text-sm text-zinc-400">Score</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{results.score?.toFixed(1) || 0}/{results.max_score || 0}</p>
                <p className="text-sm text-zinc-400">Points</p>
              </div>
              <div>
                <p className={`text-3xl font-bold ${results.passed ? 'text-green-400' : 'text-red-400'}`}>
                  {results.passed ? 'PASS' : 'FAIL'}
                </p>
                <p className="text-sm text-zinc-400">Result</p>
              </div>
            </div>
          </div>

          {/* Question Results */}
          {results.question_results && (
            <div className="space-y-4 mb-8">
              <h3 className="font-heading text-lg font-semibold text-white">Question Review</h3>
              {attempt.questions.map((question, index) => {
                const result = results.question_results[question.question_id] || {};
                return (
                  <div 
                    key={question.question_id}
                    className={`bg-navigate-surface border rounded-lg p-4 ${
                      result.correct ? 'border-green-500/30' : 'border-red-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {result.correct ? (
                        <CheckCircle className="h-5 w-5 text-green-400 mt-1 shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-400 mt-1 shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium mb-2">
                          {index + 1}. {question.question_text}
                        </p>
                        <p className="text-sm text-zinc-400">
                          Your answer: <span className={result.correct ? 'text-green-400' : 'text-red-400'}>
                            {result.user_answer || 'Not answered'}
                          </span>
                        </p>
                        {!result.correct && result.correct_answer && (
                          <p className="text-sm text-zinc-400">
                            Correct answer: <span className="text-green-400">{result.correct_answer}</span>
                          </p>
                        )}
                        {result.feedback && (
                          <p className="text-sm text-zinc-500 mt-2 italic">{result.feedback}</p>
                        )}
                      </div>
                      <span className="text-sm text-zinc-500">
                        {result.score || 0}/{result.max_score || question.points || 1} pts
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <Button onClick={() => navigate(-1)} variant="outline" className="gap-2">
              <Home className="h-4 w-4" />
              Back to Course
            </Button>
            {!results.passed && (
              <Button onClick={() => window.location.reload()} className="gap-2 bg-navigate-gold hover:bg-navigate-gold/90 text-black">
                <RotateCcw className="h-4 w-4" />
                Try Again
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Quiz Taking Screen
  return (
    <div className="min-h-screen bg-navigate-dark flex flex-col" data-testid="quiz-player">
      {/* Header */}
      <div className="bg-navigate-surface border-b border-zinc-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to leave? Your progress will be saved.')) {
                  navigate(-1);
                }
              }}
              className="p-2 text-zinc-400 hover:text-white"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-heading text-lg font-bold text-white">Quiz</h1>
              <p className="text-sm text-zinc-400">
                Question {currentQuestionIndex + 1} of {totalQuestions}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Timer */}
            {timeRemaining !== null && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                timeRemaining < 60 ? 'bg-red-500/20 text-red-400' : 'bg-zinc-800 text-white'
              }`}>
                <Clock className="h-4 w-4" />
                <span className="font-mono font-medium">{formatTime(timeRemaining)}</span>
              </div>
            )}
            
            {/* Progress */}
            <div className="text-sm text-zinc-400">
              {answeredCount}/{totalQuestions} answered
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 bg-zinc-800">
        <div 
          className="h-full bg-navigate-gold transition-all duration-300"
          style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
        />
      </div>

      {/* Question Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {currentQuestion && (
            <QuestionDisplay
              question={currentQuestion}
              questionNumber={currentQuestionIndex + 1}
              answer={answers[currentQuestion.question_id]}
              onAnswer={(answerData) => handleAnswerChange(currentQuestion.question_id, answerData)}
            />
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="bg-navigate-surface border-t border-zinc-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
            disabled={currentQuestionIndex === 0}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          {/* Question Dots */}
          <div className="flex items-center gap-1 flex-wrap justify-center max-w-md">
            {attempt.questions.map((q, idx) => (
              <button
                key={q.question_id}
                onClick={() => setCurrentQuestionIndex(idx)}
                className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                  idx === currentQuestionIndex
                    ? 'bg-navigate-gold text-black'
                    : answers[q.question_id]
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>

          {currentQuestionIndex < totalQuestions - 1 ? (
            <Button
              onClick={() => setCurrentQuestionIndex(prev => Math.min(totalQuestions - 1, prev + 1))}
              className="gap-2 bg-navigate-gold hover:bg-navigate-gold/90 text-black"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="gap-2 bg-green-600 hover:bg-green-700 text-white"
              data-testid="submit-quiz-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Quiz'}
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Question Display Component
function QuestionDisplay({ question, questionNumber, answer, onAnswer }) {
  const handleOptionSelect = (optionId) => {
    onAnswer({
      answer_type: question.question_type,
      selected_option_ids: [optionId]
    });
  };

  const handleTextAnswer = (text) => {
    onAnswer({
      answer_type: question.question_type,
      text_answer: text
    });
  };

  const selectedOptionId = answer?.selected_option_ids?.[0];
  const textAnswer = answer?.text_answer || '';

  return (
    <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6" data-testid={`question-${questionNumber}`}>
      {/* Question Text */}
      <div className="mb-6">
        <span className="text-navigate-gold text-sm font-medium">Question {questionNumber}</span>
        <h2 className="text-xl font-medium text-white mt-2">{question.question_text}</h2>
        {question.points && (
          <p className="text-sm text-zinc-500 mt-1">{question.points} point{question.points !== 1 ? 's' : ''}</p>
        )}
      </div>

      {/* Multiple Choice / True-False Options */}
      {(question.question_type === 'multiple_choice_single' || question.question_type === 'true_false') && (
        <div className="space-y-3">
          {question.options?.map((option, idx) => (
            <button
              key={option.option_id}
              onClick={() => handleOptionSelect(option.option_id)}
              className={`w-full flex items-center gap-4 p-4 rounded-lg border text-left transition-all ${
                selectedOptionId === option.option_id
                  ? 'border-navigate-gold bg-navigate-gold/10'
                  : 'border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/50'
              }`}
              data-testid={`option-${idx}`}
            >
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                selectedOptionId === option.option_id
                  ? 'border-navigate-gold bg-navigate-gold'
                  : 'border-zinc-600'
              }`}>
                {selectedOptionId === option.option_id && (
                  <CheckCircle className="h-4 w-4 text-black" />
                )}
              </div>
              <span className="text-white">{option.text}</span>
            </button>
          ))}
        </div>
      )}

      {/* Short Answer */}
      {question.question_type === 'short_answer' && (
        <div>
          <Textarea
            value={textAnswer}
            onChange={(e) => handleTextAnswer(e.target.value)}
            placeholder="Type your answer here..."
            className="bg-navigate-dark border-zinc-700 min-h-[120px]"
            data-testid="short-answer-input"
          />
        </div>
      )}
    </div>
  );
}
